"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"

import { scrollInstantly } from "../scrollInstantly"
import { isScrollIntentKey } from "../scrollIntent"
import { glideToAnchor } from "./glide"

/* EGDE-648: land an anchor target where its `scroll-margin-top` says it should,
   even when a scroll-linked transform moves it while the page is scrolling.

   The browser's native "go to #id" reads the target's CURRENT rect and scrolls
   exactly once, which is right only while the target is stationary. On a page
   with a hero sheet (HeroScrollStage) it is not: the sheet is translated by
   `-100vh * (1 - progress)`, and `progress` is recomputed from the new scroll
   position in a rAF one frame LATER. So the jump lands correctly, the stage
   then drops the sheet by up to a full viewport underneath it, and the block
   ends up off screen. Measured on the homepage at 1600x900, every block past
   the runway settled at rect.top = 1020 instead of 120.

   The cure is to keep watching for a few frames instead of trusting the first
   measurement: re-aim whenever the target has drifted off its offset, and only
   stop once it has held still long enough for the stage's own rAF to have run.
   Past the runway one correction is exact; inside it the sheet gives back half
   of every pixel scrolled, so the residual halves per pass and still converges
   in a handful of frames. The frame cap and the "scroll didn't move" guard end
   the loop for targets that can never reach the offset — a sticky hero, or a
   block within one viewport of the end of the document. */

/** Frames the target must hold its offset before we accept the landing. Must
 *  outlast the one-frame rAF the hero stage takes to react to our scroll. */
const STABLE_FRAMES = 5
/** Hard cap on the whole loop, so a fighting layout can never spin forever. */
const MAX_FRAMES = 40
/** Sub-pixel landings are exact enough — stop rather than chase rounding. */
const TOLERANCE = 1

/** Where the target wants to sit: its own `scroll-margin-top`, as the browser
 *  and `scrollIntoView({ block: "start" })` both read it. */
function offsetOf(target: Element) {
  return parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0
}

/** Attaches a one-shot "the visitor took over" abort to `fn`.
 *
 * EGDE-719 (r3 follow-up): `touchmove`, not `touchstart` — the same correction
 * the head sentinel needed, and for the same reason. Touching the screen is not
 * steering: a visitor who taps once while the page is travelling to their anchor
 * had the trip cancelled 50ms later and was left at 919px with the anchor at
 * 3283. A drag says they want the scroll; a tap says nothing. Our scrolls are
 * issued per frame rather than handed to the compositor, so a finger resting on
 * the glass does not arrest them the way it arrests a native fling — there is
 * nothing to preserve by listening earlier than the drag. */
function onVisitorScroll(fn: () => void) {
  // Only the keys that move a page count, and not while they are being typed
  // into a field — same reasoning as the touch change above.
  const onKey = (event: KeyboardEvent) => {
    if (!isScrollIntentKey(event)) return
    window.removeEventListener("keydown", onKey)
    fn()
  }
  window.addEventListener("wheel", fn, { passive: true, once: true })
  window.addEventListener("touchmove", fn, { passive: true, once: true })
  window.addEventListener("keydown", onKey)
  return () => {
    window.removeEventListener("wheel", fn)
    window.removeEventListener("touchmove", fn)
    window.removeEventListener("keydown", onKey)
  }
}

/**
 * Scrolls so `target` sits at its own `scroll-margin-top` below the viewport
 * top, re-aiming once per frame until the position holds still.
 */
export function settleAnchorScroll(target: Element | null) {
  if (typeof window === "undefined" || !target) return

  const offset = offsetOf(target)

  let frames = 0
  let stable = 0
  let raf = 0
  let lastScrollY = NaN
  let lastDelta = NaN
  let cancelled = false

  function cancel() {
    cancelled = true
    if (raf) cancelAnimationFrame(raf)
    detach()
  }

  // A visitor who starts scrolling mid-settle owns the scroll position.
  const detach = onVisitorScroll(cancel)

  const step = () => {
    raf = 0
    if (cancelled) return
    const delta = target.getBoundingClientRect().top - offset

    if (Math.abs(delta) <= TOLERANCE) {
      stable += 1
      // Held its place across enough frames that nothing else is going to
      // move it — this is where the visitor stays.
      if (stable >= STABLE_FRAMES) return detach()
    } else {
      stable = 0
      // Give up when the last correction achieved nothing: either the page
      // did not move (the scroll range ran out) or it moved and the target
      // came with it, so no amount of scrolling will close the gap — a fixed
      // or sticky target. Chasing it would drag the visitor to the end of the
      // document.
      if (
        window.scrollY === lastScrollY ||
        Math.abs(delta - lastDelta) <= TOLERANCE
      ) {
        return detach()
      }
      lastScrollY = window.scrollY
      lastDelta = delta
      // EGDE-719: `html` now carries `scroll-behavior: smooth`, and these
      // once-per-frame corrections must not inherit it — forty of them, each
      // animating, would chase the one before it. This loop's job starts where
      // the animation ends.
      scrollInstantly(() => window.scrollBy({ top: delta }))
    }

    frames += 1
    if (frames >= MAX_FRAMES) return detach()
    raf = requestAnimationFrame(step)
  }

  // Start on the next frame so the scroll that brought us here (the browser's
  // own hash jump, or a router navigation) has already been applied.
  raf = requestAnimationFrame(step)
  return cancel
}

/** Resolves a fragment (with or without its `#`) to an element, tolerating
 *  percent-encoded ids. */
function targetForHash(hash: string): Element | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash
  if (!raw) return null
  let id = raw
  try {
    id = decodeURIComponent(raw)
  } catch {
    /* Malformed escape — fall back to the raw fragment. */
  }
  return document.getElementById(id) ?? document.getElementById(raw)
}

/** Resolves the current hash to an element. */
function hashTarget(): Element | null {
  return targetForHash(window.location.hash)
}

/** How long a hash target gets to appear before the fragment is abandoned. Long
 *  enough for a client-rendered block to arrive, short enough that a fragment
 *  naming nothing does not leave a trip hanging over the page. */
const TARGET_WAIT_MS = 2000

/** Frames of an unchanged scroll position that count as "the animation is over". */
const QUIET_FRAMES = 4
/** Hard cap on the wait, so a page that never stops moving still gets pinned. */
const SCROLL_WAIT_CAP_MS = 3000
/**
 * Runs `fn` once the page has actually stopped moving.
 *
 * EGDE-719: this used to wait on `scrollend` with a fixed timeout as the
 * fallback, which was fine for a block's own short scrollIntoView but not for
 * the browser's fragment scroll — that one runs the whole distance to the
 * anchor, retargets partway when the document grows underneath it, and takes as
 * long as it takes. Any fixed fallback either fires mid-animation and cuts it
 * short, or is padding on every other caller. Watching the scroll position go
 * quiet needs no guess and behaves the same in every browser, `scrollend` or
 * not.
 *
 * A visitor who wheels away mid-animation owns the scroll from then on — their
 * first input drops the wait rather than dragging them back to the anchor they
 * just abandoned. Returns a canceller so a page leaving the screen takes its
 * pending wait with it rather than firing against a torn-down tree.
 */
function afterScrollSettles(fn: () => void) {
  let done = false
  let raf = 0
  let quiet = 0
  let last = NaN
  const deadline = performance.now() + SCROLL_WAIT_CAP_MS

  const stop = () => {
    done = true
    if (raf) cancelAnimationFrame(raf)
    detachVisitor()
  }
  const run = () => {
    if (done) return
    stop()
    fn()
  }
  const watch = () => {
    raf = 0
    if (done) return
    const y = window.scrollY
    quiet = y === last ? quiet + 1 : 0
    last = y
    if (quiet >= QUIET_FRAMES || performance.now() >= deadline) return run()
    raf = requestAnimationFrame(watch)
  }
  const detachVisitor = onVisitorScroll(stop)
  raf = requestAnimationFrame(watch)
  return stop
}

/**
 * EGDE-648: page-level hash-landing correction. Mounted once per page by
 * PageBlocks so every anchor entry point is covered:
 *   - a full load or a router navigation to /page#id (mount),
 *   - a same-document hash change (hashchange / popstate),
 *   - a click on any link carrying a fragment, including a repeat click on the
 *     hash already in the address bar and a router `Link` to /#id on the page
 *     we are already on — neither fires hashchange.
 *
 * On a page with no scroll-linked movement the first pass measures a delta of
 * zero and the component does nothing.
 */
export default function AnchorScrollSettle() {
  // EGDE-719 (r3 follow-up): this component moved from PageBlocks (which
  // remounted on every route) to the locale layout (which does not), so without
  // a dependency the mount-time correction would run once per DOCUMENT and
  // never again. A client-side navigation to /other-page#anchor would then get
  // no correction at all: the click handler bails on the differing pathname and
  // `hashchange` does not fire across paths. Next's own hash handling covers
  // the common case — measured landing exactly on target — but it aims once,
  // which is the EGDE-648 short landing whenever the new page grows after it.
  const pathname = usePathname()
  // EGDE-719 (r3 follow-up): "has the visitor navigated since this document
  // loaded", tracked across effect runs rather than in the cleanup. Flipping a
  // flag from the cleanup made React's StrictMode double-mount (dev default in
  // Next 16, and every Fast Refresh) declare the FIRST load a navigation, which
  // discarded the very scroll restoration the guard exists to protect.
  const lastPathname = useRef<string | null>(null)
  const hasNavigated = useRef(false)

  useEffect(() => {
    if (lastPathname.current !== null && lastPathname.current !== pathname) {
      hasNavigated.current = true
    }
    lastPathname.current = pathname
    const softNavigation = hasNavigated.current

    // A visitor who clicks through to a fragment has just told us where they
    // want to be, so the "they already took over" latch from earlier in this
    // document does not speak for them any more. It is a document-lifetime
    // flag set by the head sentinel and never reset, so without this the
    // cross-page correction below never ran for anyone who had scrolled once.
    if (softNavigation) {
      ;(window as { __eggedUserScrolled?: boolean }).__eggedUserScrolled = false
    }

    // EGDE-719 (r3): the inline script in the layout takes the fragment off the
    // URL before the browser can scroll to it (see PENDING_HASH_SCRIPT). Put it
    // back — `replaceState` does not scroll, so the address bar is repaired
    // without moving the page, and the journey below is this component's to
    // make from wherever the visitor actually is.
    const held = window as { __eggedPendingHash?: string }
    const pendingHash = held.__eggedPendingHash
    if (pendingHash) {
      delete held.__eggedPendingHash
      try {
        history.replaceState(
          history.state,
          "",
          window.location.pathname + window.location.search + pendingHash,
        )
      } catch {
        /* A sandboxed document may refuse — the fragment is cosmetic here, and
           the target is resolved from `pendingHash` either way. */
      }
    }

    let cancel: (() => void) | undefined
    let cancelWait: (() => void) | undefined
    const settle = (target: Element) => {
      cancel?.()
      cancel = settleAnchorScroll(target)
    }

    /**
     * Waits out whatever scroll is already running, then finishes the trip.
     *
     * EGDE-719: the browser's fragment scroll aims at the destination it can
     * see when it sets off, and on this site the document keeps growing while
     * it travels (blocks hydrate, images take their intrinsic size). Measured
     * on the homepage it arrives a full viewport short. Pinning that straight
     * away turns the animation into a glide followed by an 883px jump — the
     * same complaint in a new place. So a residual big enough to read as a jump
     * gets its own glide, and only what is left after THAT is snapped: by then
     * the layout has stopped moving, so there is nothing left to see.
     *
     * Two glides, because one is not always enough. Where the browser's scroll
     * never ran at all — a page loaded in a background tab, where there are no
     * frames for it — the first glide covers the whole page on its own and the
     * document grows just as much underneath it as it did for the browser.
     */
    /**
     * Travels to `target` and holds it there.
     *
     * EGDE-719 (r3): this used to wait for whatever scroll was running to go
     * quiet, then glide the residual, then glide again, then pin. Each wait
     * showed as a frozen screen and each glide as a fresh ease — QA filmed the
     * result twice and called it lagging both times. `glideToAnchor` re-reads
     * the anchor's position every frame instead, so a document still growing
     * underneath the trip moves the destination rather than interrupting the
     * journey. What is left when it arrives is sub-pixel, and `settle` pins
     * that instantly against the hero stage's own rAF.
     */
    const land = (target: Element) => {
      // A second anchor asked for while the first is still being held: that
      // loop's per-frame instant corrections would fight this trip all the way
      // and yank the page back to where it was aiming.
      cancel?.()
      cancelWait?.()

      let detachVisitor: (() => void) | undefined
      let stopGlide: (() => void) | undefined

      // Whichever listener fires has removed only ITSELF — `once` says nothing
      // about its two siblings, so the set has to be taken down by hand or it
      // outlives the trip and leaks for the rest of the session.
      const release = () => {
        detachVisitor?.()
        detachVisitor = undefined
      }

      // Attached BEFORE the trip starts, because the trip can finish inside its
      // own call: with `prefers-reduced-motion` set, `glideToAnchor` scrolls and
      // calls back synchronously. Reading `detachVisitor` from that callback
      // while it was still in its temporal dead zone threw `ReferenceError` on
      // every reduced-motion landing — optional chaining does not soften a TDZ —
      // and the throw escaped into the click handler, leaving `settle` unrun and
      // the anchor unpinned against the hero stage's rAF.
      //
      // A visitor who takes the wheel mid-trip owns the scroll from then on,
      // rather than being carried the rest of the way to an anchor they have
      // just steered away from.
      detachVisitor = onVisitorScroll(() => {
        release()
        stopGlide?.()
      })

      stopGlide = glideToAnchor(target, () => {
        release()
        settle(target)
      })

      cancelWait = () => {
        stopGlide?.()
        release()
      }
    }

    // Bubble phase, so anything that runs its own scroll (the anchor rail, the
    // article TOC, the router) has already claimed the click and we can tell.
    const onClick = (e: MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return
      const link = (e.target as Element | null)?.closest?.("a[href]")
      if (!link) return
      const href = link.getAttribute("href") || ""
      const hash = href.split("#")[1]
      if (!hash) return
      // Only same-page fragments. A link to another route happens to be able to
      // name an id that also exists here; scrolling this page on the way out
      // would be pure noise, and the destination settles on mount anyway.
      let url: URL
      try {
        url = new URL(href, window.location.href)
      } catch {
        return
      }
      if (
        url.pathname !== window.location.pathname ||
        url.origin !== window.location.origin
      ) {
        return
      }
      let target: Element | null = null
      try {
        target = document.getElementById(decodeURIComponent(hash))
      } catch {
        /* Malformed escape — fall through to the raw fragment. */
      }
      target ??= document.getElementById(hash)
      if (!target) return
      // Either a block is animating the scroll itself (it claimed the click) or
      // the browser is — since EGDE-719 its fragment scroll is animated too. Both
      // align on `scroll-margin-top`, so both get the same treatment: let the
      // travel arrive, then finish the trip. On a page that does not move
      // underfoot this measures no drift and leaves the landing where they put it.
      land(target)
    }

    // The browser scrolls to the new fragment itself — animated, since
    // EGDE-719 — so this is the click case again: wait for the travel, then
    // finish the trip. Pinning on the next frame instead would land the
    // correction mid-animation and cut it short.
    const onHashNav = () => {
      const target = hashTarget()
      if (target) {
        land(target)
        return
      }
      // Navigated to a hash that resolves to nothing — /#faq back to / , say.
      // Any loop still holding the old anchor has to be let go, or it keeps
      // re-aiming and pulls the page back down after the fragment is gone.
      cancel?.()
      cancelWait?.()
    }

    // EGDE-719 (r2): whatever the mount heuristics below conclude, a visitor
    // who has already scrolled or typed owns the position — full stop. The
    // abort listeners this component attaches can only see input from
    // hydration onwards, and on a slow connection hydration lands many seconds
    // after the visitor started reading (measured at 1.2Mbit: input at 4.5s,
    // hydration at 15.5s — and the "finish the trip" glide then dragged them
    // back to an anchor they had deliberately left, which is QA's video). The
    // sentinel is an inline script in the document head (see
    // USER_INPUT_SENTINEL_SCRIPT in app/[locale]/layout.tsx), so it predates
    // anything the visitor can do.
    const visitorTookOver = () =>
      (window as { __eggedUserScrolled?: boolean }).__eggedUserScrolled === true

    // A hash in the URL does not mean we just arrived at it. Coming BACK to a
    // hashed URL — or reloading one — restores the scroll position the visitor
    // left, and re-forcing the anchor would throw that away. So how we arrived
    // decides, not where the page happens to be:
    //
    //   - a fresh navigation to a hashed URL -> the fragment is the whole
    //     instruction, and since r3 the browser has not acted on it (the layout
    //     holds it back). Make the journey here, to where the anchor really is.
    //   - `reload` or `back_forward` -> the browser is restoring a position the
    //     visitor chose. Keep out of the way, unless the page is still at the
    //     very top, where there is nothing to preserve. That reading has to wait
    //     for the restore to finish (Chrome does it in stages), which costs
    //     nothing: this branch does not animate.
    //
    // EGDE-719 (r3): the fresh-navigation branch no longer waits for the page to
    // go quiet first. Waiting was what made the trip stop halfway — the wait
    // could only end after the browser's scroll had, and by then the screen had
    // been still long enough to read as a stall. There is nothing left to wait
    // for now that the trip is ours from the start. The drift tolerance that
    // used to guard this branch went with it; `browserOwnsThePosition` already
    // covers every case it was there for, and unlike the tolerance it cannot
    // mistake a restored position for a missed landing (measured in r2: Back to
    // /#FaqAnchor restored to 6010, 647px from the anchor and well inside the
    // tolerance, so the "correction" fired and threw the restore away).
    //
    // The fragment the landing below aims at. `location.hash` normally carries
    // it, but when `replaceState` above was refused — a sandboxed document, and
    // this app has an embed mode — the inline script has already stripped it and
    // only `pendingHash` still knows what was asked for. Without this fallback
    // that case lost the anchor twice over: no fragment in the URL AND no trip.
    const mountHash = window.location.hash || pendingHash || ""
    let mountRaf = 0
    if (mountHash) {
      const [nav] = performance.getEntriesByType(
        "navigation",
      ) as PerformanceNavigationTiming[]
      // A held fragment is a fresh navigation by construction — the inline
      // script only holds those back. A soft navigation is one too, and the
      // navigation entry still describes the ORIGINAL document load, so it must
      // not be consulted on a re-run.
      const browserOwnsThePosition =
        !softNavigation && !pendingHash && !!nav && nav.type !== "navigate"
      // EGDE-719 (r3): the target is looked for over a short window rather than
      // once. Every anchor on this site is server-rendered, so it is normally
      // there on the first look — but the browser used to be the backstop for
      // the ones that are not (it scrolls whenever it meets the element during
      // load), and holding the fragment back took that away. A block that
      // renders on the client only would otherwise leave the visitor at the top
      // with no way to tell anything was meant to happen.
      const deadline = performance.now() + TARGET_WAIT_MS
      const attempt = () => {
        mountRaf = 0
        if (visitorTookOver()) return
        const target = targetForHash(mountHash)
        if (!target) {
          if (performance.now() < deadline) {
            mountRaf = requestAnimationFrame(attempt)
          }
          return
        }
        if (!browserOwnsThePosition) {
          cancelWait?.()
          land(target)
          return
        }
        cancelWait?.()
        cancelWait = afterScrollSettles(() => {
          if (visitorTookOver()) return
          if (window.scrollY <= 0) land(target)
        })
      }
      // Two frames before the first look: a router navigation scrolls after
      // render rather than before the effect runs.
      mountRaf = requestAnimationFrame(() => {
        mountRaf = requestAnimationFrame(attempt)
      })
    }

    // `hashchange` already covers same-document history moves between hashes;
    // `popstate` would add nothing but the restore case guarded against above.
    window.addEventListener("hashchange", onHashNav)
    document.addEventListener("click", onClick)
    return () => {
      cancel?.()
      cancelWait?.()
      cancelAnimationFrame(mountRaf)
      window.removeEventListener("hashchange", onHashNav)
      document.removeEventListener("click", onClick)
    }
  }, [pathname])

  return null
}
